import { useEffect, useState } from "react";
import { Loader2, Save, ExternalLink, UploadCloud } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FIELDS_TOP = [
  { key: "service_name", label: "サービス名", ph: "Welfy Care Campus" },
  { key: "tagline", label: "キャッチコピー", ph: "障がい福祉の研修・法定管理を、ひとつに。" },
  { key: "hero_title", label: "ヒーロー見出し", ph: "障がい福祉の研修・法定管理を、ひとつに。" },
  { key: "hero_description", label: "ヒーロー説明文", ph: "サービスの概要説明", area: true },
  { key: "company_name", label: "会社名（運営会社）", ph: "Welfy Care Campus 運営事務局" },
];
const FIELDS_BOTTOM = [
  { key: "address", label: "住所", ph: "東京都◯◯区…" },
  { key: "phone", label: "電話番号", ph: "03-0000-0000" },
  { key: "email", label: "メールアドレス", ph: "info@example.com" },
];
const PRESET_ROLES = ["代表者", "代表取締役", "代表取締役社長", "代表理事", "理事長", "会長", "施設長", "管理者"];

export default function PlatformLPSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customRole, setCustomRole] = useState(false);
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onRoleChange = (v) => {
    if (v === "__custom__") { setCustomRole(true); setField("representative_title", ""); }
    else { setCustomRole(false); setField("representative_title", v); }
  };

  useEffect(() => {
    api.get("/platform/lp-settings").then(({ data }) => {
      setForm(data);
      setCustomRole(!!data.representative_title && !PRESET_ROLES.includes(data.representative_title));
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/platform/lp-settings", form);
      toast.success("ランディングページ設定を保存しました");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/platform/upload", fd);
      setForm((p) => ({ ...p, logo_image: data.url }));
      toast.success("ロゴ画像をアップロードしました");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!form) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;

  return (
    <div data-testid="platform-lp-settings" className="max-w-3xl">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">LP（ランディングページ）設定</h1>
          <p className="text-sm text-slate-400 mt-1">公開サイトの文言・会社情報・連絡先を編集します</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" data-testid="lp-settings-preview"
          className="inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300">
          <ExternalLink className="w-4 h-4" /> 公開ページを開く
        </a>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 space-y-5">
        {FIELDS_TOP.map((f) => (
          <div key={f.key}>
            <Label className="text-slate-300">{f.label}</Label>
            {f.area ? (
              <Textarea data-testid={`lp-field-${f.key}`} value={form[f.key] || ""} rows={4}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.ph} className="mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
            ) : (
              <Input data-testid={`lp-field-${f.key}`} value={form[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                placeholder={f.ph} className="mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
            )}
          </div>
        ))}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300">代表者名</Label>
            <Input data-testid="lp-field-representative" value={form.representative || ""}
              onChange={(e) => setField("representative", e.target.value)}
              placeholder="山田 太郎" className="mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
          </div>
          <div>
            <Label className="text-slate-300">代表者の肩書</Label>
            <Select value={customRole ? "__custom__" : (PRESET_ROLES.includes(form.representative_title) ? form.representative_title : "__custom__")} onValueChange={onRoleChange}>
              <SelectTrigger className="mt-1.5 bg-slate-800 border-slate-700 text-white" data-testid="lp-role-select"><SelectValue placeholder="肩書きを選択" /></SelectTrigger>
              <SelectContent>
                {PRESET_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                <SelectItem value="__custom__">自由入力</SelectItem>
              </SelectContent>
            </Select>
            {(customRole || !PRESET_ROLES.includes(form.representative_title)) && (
              <Input data-testid="lp-field-representative-title" value={form.representative_title || ""}
                onChange={(e) => setField("representative_title", e.target.value)}
                placeholder="肩書きを入力（例：園長）" className="mt-2 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
            )}
          </div>
        </div>
        {FIELDS_BOTTOM.map((f) => (
          <div key={f.key}>
            <Label className="text-slate-300">{f.label}</Label>
            <Input data-testid={`lp-field-${f.key}`} value={form[f.key] || ""}
              onChange={(e) => setField(f.key, e.target.value)}
              placeholder={f.ph} className="mt-1.5 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
          </div>
        ))}
        <div className="border-t border-slate-800 pt-4">
          <Label className="text-slate-300">ロゴマーク（修了証の発行者ロゴ）</Label>
          <div className="flex items-center gap-3 mt-2">
            {form.logo_image
              ? <img src={`${BACKEND}${form.logo_image}`} alt="logo" className="w-16 h-16 object-contain bg-white rounded border border-slate-700" data-testid="lp-logo-thumb" />
              : <div className="w-16 h-16 rounded border border-dashed border-slate-700 flex items-center justify-center text-slate-500 text-xs">なし</div>}
            <label className="cursor-pointer">
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="lp-logo-upload" />
              <span className="inline-flex items-center px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-slate-200 text-sm hover:bg-slate-700"><UploadCloud className="w-4 h-4 mr-1" />画像をアップロード</span>
            </label>
            {form.logo_image && <button onClick={() => setForm((p) => ({ ...p, logo_image: "" }))} className="text-slate-400 hover:text-rose-400 text-sm" data-testid="lp-logo-remove">削除</button>}
          </div>
          <p className="text-xs text-slate-500 mt-2">修了証明書の発行者（運営会社）情報の左横に表示されます。</p>
        </div>
        <div className="pt-2">
          <Button onClick={save} disabled={busy} data-testid="lp-settings-save"
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> 保存する</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
